import React, { useEffect, useState } from "react";
import { Layout, Menu, Empty, Spin, Typography, Drawer, Button } from "antd";
import {
  ApartmentOutlined, CompassOutlined, FolderOutlined, FileImageOutlined, PartitionOutlined,
  MenuOutlined, DatabaseOutlined,
  ProfileOutlined, SolutionOutlined, TeamOutlined, TagOutlined, FontSizeOutlined,
  SwapOutlined, SafetyCertificateOutlined, BookOutlined, FolderOpenOutlined,
} from "@ant-design/icons";
import SymbologyTree from "./SymbologyTree.js";
import LevelOfInformationTree from "./LevelOfInformationTree.js";
import DictionaryView from "./DictionaryView.js";
import MarkdownContent from "./MarkdownContent.js";
import { slugifyTerm } from "./dictionaryUtils.js";
import useIsMobile from "../../hooks/useIsMobile.js";

const { Sider, Content } = Layout;
const { Title } = Typography;

// Sections with a bespoke layout instead of the generic markdown + stacked-image gallery.
// Symbology fetches its own data (a separate endpoint entirely — see SymbologyTree.js) and
// skips the generic content fetch below. Level of Information reuses the same description the
// generic fetch already gets (an intro blurb from its own folder's .md), just laid out
// differently (see LevelOfInformationTree.js), so it isn't skipped — it additionally fetches
// its own item type data separately, on top of that shared description fetch.
// (Custom Menus/Keyboard Shortcuts and Superelevation used to live here too — both moved to the
// Learn page, since both are design-software usage/configuration references (ORD's own keyboard
// shortcuts, ORD's superelevation calculation XML parameters), not BEP-facing project
// documentation. KeyboardShortcutsView.js stays in this folder and is now imported by
// Learn/index.js instead; Superelevation renders through the generic MarkdownContent path on
// both pages, so it needed no bespoke component to move.)
const SYMBOLOGY_KEY = "Disciplines/Transportation/Symbology";
const DICTIONARY_KEY = "Dictionary";
// Matched by suffix, not one hardcoded discipline path — any future Disciplines/<name>/Level of
// Information folder gets the same bespoke tree with no code change, same spirit as
// getDisciplineLinks() below already being discipline-agnostic.
const isLevelOfInformationKey = (key) => !!key && key.endsWith("/Level of Information");

// Curated icons for the known headlines; anything new the user adds later (new folder =
// new headline/sub-discipline, fully dynamic) falls back to a generic folder/page icon
// instead of breaking.
const ICONS = {
  "Overview":                                          <ProfileOutlined />,
  "BEP Content":                                        <SolutionOutlined />,
  "BIM Roles and Responsibilities":                    <TeamOutlined />,
  "Naming Conventions":                                <FontSizeOutlined />,
  "Document Metadata Specifications":                  <TagOutlined />,
  "Information Delivery & Exchange Procedures":        <SwapOutlined />,
  "Design Standarts":                                  <SafetyCertificateOutlined />,
  "Disciplines":                    <ApartmentOutlined />,
  "Transportation":                 <CompassOutlined />,
  "Symbology":                      <PartitionOutlined />,
  "Level of Information":           <DatabaseOutlined />,
  "Dictionary":                     <BookOutlined />,
  "Resources":                      <FolderOpenOutlined />,
};
const iconFor = (label, hasChildren) => ICONS[label] || (hasChildren ? <FolderOutlined /> : <FileImageOutlined />);

// Nodes with children become expandable Menu groups (not directly selectable — they're
// category headers, e.g. "Disciplines"); leaf nodes are the actual selectable content pages.
function buildMenuItems(nodes) {
  return nodes.map((n) => {
    const hasChildren = n.children && n.children.length > 0;
    return {
      key:   n.key,
      icon:  iconFor(n.label, hasChildren),
      label: n.label,
      ...(hasChildren ? { children: buildMenuItems(n.children) } : {}),
    };
  });
}

function firstLeafKey(nodes) {
  for (const n of nodes) {
    if (!n.children || n.children.length === 0) return n.key;
    const found = firstLeafKey(n.children);
    if (found) return found;
  }
  return null;
}

// Design Standarts' own description.md cross-links to each discipline's Symbology section
// under a "## Disciplines" heading (see MarkdownContent's DISCIPLINE_LINKS_MARKER) instead of
// hardcoding "Transportation" by name — derived from the live tree so a future discipline
// (Rail's own item types, a second corridor, whatever) shows up automatically the moment its
// folder gains a "Symbology" child, no code change needed.
function getDisciplineLinks(tree) {
  const disciplines = tree.find((n) => n.label === "Disciplines");
  if (!disciplines || !disciplines.children) return [];
  return disciplines.children
    .map((disc) => {
      const symbology = (disc.children || []).find((c) => c.label === "Symbology");
      return symbology ? { key: symbology.key, label: disc.label } : null;
    })
    .filter(Boolean);
}

const encodeSection = (key) => key.split("/").map(encodeURIComponent).join("/");

export default function BIMFramework() {
  const [tree, setTree]                 = useState([]);
  const [treeLoading, setTreeLoading]   = useState(true);
  const [selectedKey, setSelectedKey]   = useState(null);
  const [content, setContent]           = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  // Set only right after a "Related terms" tag is clicked (see MarkdownContent's
  // onNavigateToDictionaryTerm below) — cleared on any ordinary left-nav click, so Dictionary's
  // "Back to X" button and term-scroll only ever appear right after actually following a
  // cross-reference, never as stale state from an earlier visit.
  const [dictionaryNav, setDictionaryNav] = useState(null);
  const isMobile = useIsMobile();
  // Left nav is a push-panel Sider on desktop (always visible) but an overlay Drawer on
  // mobile — 286px is most of a phone screen, so it starts closed and only opens on demand.
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    fetch("/api/bimframework/tree")
      .then((r) => r.json())
      .then((data) => {
        const t = data.tree || [];
        setTree(t);
        setSelectedKey(firstLeafKey(t));
      })
      .catch(() => setTree([]))
      .finally(() => setTreeLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedKey || selectedKey === SYMBOLOGY_KEY) { setContent(null); return; }
    setContentLoading(true);
    fetch(`/api/bimframework/content/${encodeSection(selectedKey)}`)
      .then((r) => r.json())
      .then(setContent)
      .catch(() => setContent({ description: "", images: [] }))
      .finally(() => setContentLoading(false));
  }, [selectedKey]);

  const menuItems       = buildMenuItems(tree);
  const sectionTitle    = selectedKey ? selectedKey.split("/").pop() : null;
  const disciplineLinks = getDisciplineLinks(tree);
  const goToSection = (key) => { setDictionaryNav(null); setSelectedKey(key); };

  const menuBody = (
    <>
      <div style={{ padding: "26px 20px 18px" }}>
        <Title level={4} style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          BIM <em style={{ fontStyle: "normal", color: "#4f9cf9" }}>Framework</em>
        </Title>
      </div>
      {treeLoading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
      ) : tree.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty description="No sections yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <Menu
          mode="inline"
          theme="dark"
          style={{ background: "transparent", borderRight: "none" }}
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={({ key }) => { setDictionaryNav(null); setSelectedKey(key); setNavOpen(false); }}
        />
      )}
    </>
  );

  // Mobile: hamburger button in front of the section title opens the nav Drawer — no
  // dedicated header bar exists on this page otherwise, so it rides along with the title
  // that every content branch below already renders.
  const sectionHeader = (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
      {isMobile && (
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setNavOpen(true)}
          style={{ flexShrink: 0, marginLeft: -8 }}
        />
      )}
      <Title level={3} style={{ margin: 0 }}>{sectionTitle}</Title>
    </div>
  );

  return (
    <Layout style={{ height: "100%", background: "transparent" }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width="82%"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          closable={false}
          styles={{ body: { padding: 0 }, header: { display: "none" } }}
        >
          {menuBody}
        </Drawer>
      ) : (
        <Sider width={286} style={{ background: "rgba(255,255,255,0.02)", borderRight: "1px solid rgba(255,255,255,0.08)" }}>
          {menuBody}
        </Sider>
      )}

      <Content style={{ overflowY: "auto", padding: isMobile ? "20px 16px 40px" : "40px 56px" }}>
        {contentLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : !selectedKey ? (
          <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
            <Empty description="No sections yet — add a folder under pages/bimframework/" />
          </div>
        ) : selectedKey === SYMBOLOGY_KEY ? (
          <div style={{ maxWidth: 1100 }}>
            {sectionHeader}
            <SymbologyTree />
          </div>
        ) : isLevelOfInformationKey(selectedKey) ? (
          <div style={{ maxWidth: 1200 }}>
            {sectionHeader}
            <LevelOfInformationTree selectedKey={selectedKey} encodeSection={encodeSection} description={content?.description} />
          </div>
        ) : selectedKey === DICTIONARY_KEY ? (
          <div style={{ maxWidth: 800 }}>
            {sectionHeader}
            <DictionaryView
              content={content}
              focusTerm={dictionaryNav?.slug || null}
              returnTo={dictionaryNav ? { key: dictionaryNav.returnKey, label: dictionaryNav.returnLabel } : null}
              onReturn={() => { setSelectedKey(dictionaryNav.returnKey); setDictionaryNav(null); }}
            />
          </div>
        ) : (
          <div style={{ maxWidth: 900 }}>
            {sectionHeader}

            {content?.description || content?.images?.length > 0 || content?.files?.length > 0 ? (
              <MarkdownContent
                text={content.description}
                images={content.images}
                files={content.files}
                imageUrlFor={(img) => `/api/bimframework/image/${encodeSection(selectedKey)}/${encodeURIComponent(img.name)}?v=${img.version}`}
                downloadUrlFor={(file) => `/api/bimframework/download/${encodeSection(selectedKey)}/${encodeURIComponent(file.name)}`}
                onNavigateToDictionaryTerm={(termText) => {
                  setDictionaryNav({ slug: slugifyTerm(termText), returnKey: selectedKey, returnLabel: sectionTitle });
                  setSelectedKey(DICTIONARY_KEY);
                }}
                disciplineLinks={disciplineLinks}
                onNavigateToSection={goToSection}
              />
            ) : (
              <Empty description="No images uploaded for this section yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
            )}
          </div>
        )}
      </Content>
    </Layout>
  );
}
